import {
  contactSmsStreamFrameSchema,
  type ContactSmsChangeFrame,
} from "@/lib/data/live-updates/contact-sms/contract";
import {
  createDurableLiveUpdatesController,
  type DurableLiveUpdateStatus,
} from "@/lib/data/live-updates/controller";
import {
  retrieveContactSmsV2,
  retrieveSmsMediaPreview,
  sendSmsV1,
  type ContactSmsPage,
} from "@/lib/data/requests/contacts/contact-sms";
import { generateS3ItemPreSignedUrlV1 } from "@/lib/data/requests/s3";
import { VITE_FAST_API_URL } from "@/lib/data/static/importEnv";
import { queryKeys } from "@/lib/data/static/query-keys";
import type { APIError, APIResponse } from "@/lib/types/APIResponse";
import { toastAPIErrorMessage } from "@/lib/utils/axiosUtils";
import { fetchAuthSession } from "@aws-amplify/auth";
import {
  type InfiniteData,
  useMutation,
  useQueryClient,
} from "@tanstack/react-query";
import type { AxiosError } from "axios";
import { useEffect, useRef, useState } from "react";
import { toast } from "react-hot-toast";

const contactSmsStreamSubprotocol = "lawworks-api.contact-sms.v2";

export const smsMediaPreviewQueryOptions = (s3Key: string) => ({
  queryKey: [queryKeys.contact, "sms-media-preview", s3Key],
  queryFn: ({ signal }: { signal: AbortSignal }) =>
    retrieveSmsMediaPreview(s3Key, signal),
  staleTime: 4 * 60 * 1000,
  gcTime: 5 * 60 * 1000,
  refetchOnWindowFocus: false,
});
const tokenRefreshWindowMs = 60_000;

export const contactSmsQueryKey = (contactId: string) =>
  [queryKeys.contact, contactId, "sms"] as const;

export const retrieveContactSmsInfiniteQueryOptions = (contactId: string) => ({
  queryKey: contactSmsQueryKey(contactId),
  queryFn: ({ pageParam }: { pageParam: string | null }) =>
    retrieveContactSmsV2({
      contactId,
      cursor: pageParam ?? undefined,
    }),
  initialPageParam: null as string | null,
  getNextPageParam: (lastPage: APIResponse<ContactSmsPage>) =>
    lastPage.pagination?.nextCursor ?? undefined,
});

const buildContactSmsStreamUrl = (contactId: string, after: string) => {
  if (typeof VITE_FAST_API_URL !== "string" || !VITE_FAST_API_URL) {
    throw new Error("Missing FastAPI URL");
  }
  const url = new URL(
    `/contacts/v2/${contactId}/communications/sms/stream`,
    VITE_FAST_API_URL,
  );
  url.protocol = url.protocol === "https:" ? "wss:" : "ws:";
  url.searchParams.set("after", after);
  return url.toString();
};

const getAccessToken = async (forceRefresh = false) => {
  let session = await fetchAuthSession();
  const expiresAt = session.tokens?.accessToken.payload.exp;
  if (
    forceRefresh ||
    !expiresAt ||
    expiresAt * 1_000 - Date.now() <= tokenRefreshWindowMs
  ) {
    session = await fetchAuthSession({ forceRefresh: true });
  }
  const token = session.tokens?.accessToken.toString();
  if (!token) throw new Error("Missing Cognito access token");
  return token;
};

type ContactSmsInfiniteData = InfiniteData<
  APIResponse<ContactSmsPage>,
  string | null
>;

/** Keeps one open contact conversation current without polling the full contact. */
export const useContactSmsLiveUpdates = (
  contactId: string,
  initialCursor?: string,
) => {
  const queryClient = useQueryClient();
  const [status, setStatus] = useState<DurableLiveUpdateStatus>("connecting");
  const bootstrap = useRef<{ contactId: string; cursor: string } | null>(null);
  if (bootstrap.current?.contactId !== contactId) bootstrap.current = null;
  if (!bootstrap.current && initialCursor) {
    bootstrap.current = { contactId, cursor: initialCursor };
  }
  const connectionCursor = bootstrap.current?.cursor;

  useEffect(() => {
    if (!connectionCursor) return;

    const storeNewestPage = (
      snapshot: APIResponse<ContactSmsPage>,
      reset: boolean,
    ) => {
      queryClient.setQueryData<ContactSmsInfiniteData>(
        contactSmsQueryKey(contactId),
        (current) => ({
          pageParams: reset || !current ? [null] : current.pageParams,
          pages:
            reset || !current
              ? [snapshot]
              : [snapshot, ...current.pages.slice(1)],
        }),
      );
    };

    const retrieveNewestPage = async (signal: AbortSignal, reset: boolean) => {
      const snapshot = await retrieveContactSmsV2({ contactId, signal });
      const cursor = snapshot.pagination?.liveUpdateCursor;
      if (!cursor)
        throw new Error("Contact SMS snapshot omitted its live cursor");
      storeNewestPage(snapshot, reset);
      return { cursor };
    };

    const controller = createDurableLiveUpdatesController({
      buildUrl: (after) => buildContactSmsStreamUrl(contactId, after),
      getAccessToken,
      initialCursor: connectionCursor,
      onChanges: async (
        _changes: ContactSmsChangeFrame[],
        signal: AbortSignal,
      ) => {
        await retrieveNewestPage(signal, true);
      },
      onStatusChange: setStatus,
      parseFrame: (value) => {
        const result = contactSmsStreamFrameSchema.safeParse(value);
        return result.success ? result.data : undefined;
      },
      protocol: contactSmsStreamSubprotocol,
      refreshSnapshot: (signal) => retrieveNewestPage(signal, true),
    });

    controller.start();
    const reconnectWhenOnline = () => {
      controller.reconnectNow();
    };
    window.addEventListener("online", reconnectWhenOnline);
    return () => {
      window.removeEventListener("online", reconnectWhenOnline);
      controller.stop();
    };
  }, [connectionCursor, contactId, queryClient]);

  return status;
};

export const useSendSms = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: Parameters<typeof sendSmsV1>[0]) => sendSmsV1(data),
    onError: (error: AxiosError<APIError>) => {
      toastAPIErrorMessage(error, "Error sending SMS");
    },
    onSettled: async (_response, _error, variables) => {
      await queryClient.invalidateQueries({
        queryKey: contactSmsQueryKey(variables.contactId),
      });
    },
    onSuccess: () => {
      toast.success("SMS sent successfully");
    },
  });
};

export const useCreateMessageMediaObject = () => {
  return useMutation({
    mutationFn: async (data: { file: File }) => {
      const presignedURLRes = await generateS3ItemPreSignedUrlV1(
        "message-media",
      )({
        fileName: data.file.name,
        clientMethod: "put_object",
      });

      await fetch(presignedURLRes.data.presignedUrl, {
        method: "PUT",
        headers: {
          "Content-Type": data.file.type,
        },
        body: data.file,
      });

      return presignedURLRes.data;
    },
  });
};
